import { useState, useMemo } from "react";
import type { WeeklyProgressVM } from "./types";
import { categoryStrokeAlpha } from "./categoryColors";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Props {
  vm: WeeklyProgressVM;
}

export function HeatmapTab({ vm }: Props) {
  const { days, categoryOrder, categoryColors, heatmapMatrix } = vm;
  const [focusedCat, setFocusedCat] = useState<string | null>(null);

  // Max value for intensity scaling
  const maxVal = useMemo(() => {
    let m = 1;
    for (const row of heatmapMatrix) for (const v of row) if (v > m) m = v;
    return m;
  }, [heatmapMatrix]);

  // Summary
  const mostActiveDay = useMemo(() => {
    let best = days[0];
    for (const d of days) {
      if ((d._totalTasks as number) > ((best?._totalTasks as number) ?? 0))
        best = d;
    }
    return best;
  }, [days]);

  const totalCategories = categoryOrder.length;

  const getCellAlpha = (val: number) => {
    if (val === 0) return 0;
    const steps = [0.25, 0.5, 0.75, 1];
    const idx = Math.min(val - 1, steps.length - 1);
    return steps[idx];
  };

  return (
    <div className="space-y-2">
      {/* Summary */}
      <div className="flex gap-4 px-2 text-[10px] text-muted-foreground">
        <span>Kategorier brukt: {totalCategories}</span>
        {mostActiveDay && (mostActiveDay._totalTasks as number) > 0 && (
          <span>
            Mest aktive dag: {mostActiveDay.label} ({mostActiveDay._totalTasks as number})
          </span>
        )}
      </div>

      {/* Heatmap grid */}
      <div className="overflow-x-auto px-1">
        <TooltipProvider delayDuration={0}>
          <table className="w-full border-separate" style={{ borderSpacing: "2px" }}>
            <thead>
              <tr>
                <th className="text-left text-[10px] text-muted-foreground font-normal w-20 min-w-[4rem]" />
                {days.map((d) => (
                  <Tooltip key={d.date}>
                    <TooltipTrigger asChild>
                      <th className="text-center text-[9px] text-muted-foreground font-normal px-0 cursor-default">
                        {d.label}
                      </th>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      Totalt: {d._totalTasks as number}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </tr>
            </thead>
            <tbody>
              {categoryOrder.map((cat, catIdx) => {
                const dimmed = focusedCat !== null && focusedCat !== cat;
                return (
                  <tr
                    key={cat}
                    style={{ opacity: dimmed ? 0.2 : 1 }}
                    className="transition-opacity"
                  >
                    <td
                      className="text-[10px] text-muted-foreground truncate max-w-[5rem] cursor-pointer pr-1"
                      onClick={() =>
                        setFocusedCat((prev) => (prev === cat ? null : cat))
                      }
                    >
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full mr-1"
                        style={{ backgroundColor: categoryColors[cat].stroke }}
                      />
                      {cat}
                    </td>
                    {heatmapMatrix[catIdx].map((val, dayIdx) => (
                      <Tooltip key={dayIdx}>
                        <TooltipTrigger asChild>
                          <td
                            className="rounded-sm cursor-default"
                            style={{
                              backgroundColor:
                                val === 0
                                  ? "hsl(var(--muted) / 0.3)"
                                  : categoryStrokeAlpha(
                                      categoryColors[cat].stroke,
                                      getCellAlpha(val)
                                    ),
                              width: "1.2rem",
                              height: "1.2rem",
                              minWidth: "1.2rem",
                            }}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          {days[dayIdx].label} · {cat}: {val}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TooltipProvider>
      </div>
    </div>
  );
}
